import React, { useState, useRef, useCallback } from 'react';

interface DraggableGalleryProps {
  urls: string[];
  onChange: (urls: string[]) => void;
}

export function DraggableGallery({ urls, onChange }: DraggableGalleryProps) {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  const dragNode = useRef<HTMLDivElement | null>(null);

  const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
    setDragIndex(index);
    dragNode.current = e.currentTarget as HTMLDivElement;
    e.dataTransfer.effectAllowed = 'move';
    // Make the drag image slightly transparent
    setTimeout(() => {
      if (dragNode.current) {
        dragNode.current.style.opacity = '0.4';
      }
    }, 0);
  }, []);

  const handleDragEnd = useCallback(() => {
    if (dragNode.current) {
      dragNode.current.style.opacity = '1';
    }
    setDragIndex(null);
    setOverIndex(null);
    dragNode.current = null;
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setOverIndex(index);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === dropIndex) {
      handleDragEnd();
      return;
    }

    const newUrls = [...urls];
    const [dragged] = newUrls.splice(dragIndex, 1);
    newUrls.splice(dropIndex, 0, dragged);
    onChange(newUrls);
    handleDragEnd();
  }, [dragIndex, urls, onChange, handleDragEnd]);

  const handleRemove = useCallback((index: number) => {
    onChange(urls.filter((_, i) => i !== index));
  }, [urls, onChange]);

  if (urls.length === 0) return null;

  return (
    <div className="space-y-2">
      <p className="text-xs text-gray-500">Drag photos to reorder</p>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
        {urls.map((url, i) => (
          <div
            key={`${url}-${i}`}
            draggable
            onDragStart={(e) => handleDragStart(e, i)}
            onDragEnd={handleDragEnd}
            onDragOver={(e) => handleDragOver(e, i)}
            onDrop={(e) => handleDrop(e, i)}
            className={`relative rounded-lg overflow-hidden cursor-grab active:cursor-grabbing group transition-all duration-150 ${
              overIndex === i && dragIndex !== i
                ? 'ring-2 ring-violet-400 ring-offset-2 scale-[1.02]'
                : ''
            } ${dragIndex === i ? 'opacity-40' : ''}`}
          >
            <img
              src={url}
              alt={`Gallery ${i + 1}`}
              className="w-full h-24 object-cover pointer-events-none"
              draggable={false}
            />
            {/* Order indicator */}
            <div className="absolute bottom-1 left-1 bg-black/60 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
              {i + 1}
            </div>
            {/* Drag handle indicator */}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
              <svg
                className="w-5 h-5 text-white opacity-0 group-hover:opacity-70 transition-opacity drop-shadow-lg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
              </svg>
            </div>
            {/* Remove button */}
            <button
              onClick={(e) => { e.stopPropagation(); handleRemove(i); }}
              className="absolute top-1 right-1 bg-red-500 text-white p-0.5 rounded-full hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
